// Sign-in is only offered for Adobe accounts registered in the United States
// or Canada; accounts registered in any other *recognized* country —
// including the UK, Switzerland, and the entire EU/EEA — are blocked.
// Matching is exact ISO 3166-1 alpha-2, so US territories with their own
// codes (PR, GU, VI, AS) are blocked too. That is a deliberate decision;
// adding a code to this set is the one-line change if it ever flips.
export const ALLOWED_COUNTRY_CODES: ReadonlySet<string> = new Set(['US', 'CA']);

// Every officially assigned ISO 3166-1 alpha-2 code (249 as of the current
// ISO list). Blocking requires the countryCode to be positively identified
// against this set: the first rollout of this gate blocked anything that
// wasn't US/CA and locked out users whose IMS countryCode carried values we
// never anticipated, which is what got it reverted. Hardcoded rather than
// derived from Intl.DisplayNames so the gate behaves identically in every
// browser and is exactly testable.
const ISO_3166_1_ALPHA_2 =
	'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ ' +
	'BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ ' +
	'CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ ' +
	'DE DJ DK DM DO DZ ' +
	'EC EE EG EH ER ES ET ' +
	'FI FJ FK FM FO FR ' +
	'GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY ' +
	'HK HM HN HR HT HU ' +
	'ID IE IL IM IN IO IQ IR IS IT ' +
	'JE JM JO JP ' +
	'KE KG KH KI KM KN KP KR KW KY KZ ' +
	'LA LB LC LI LK LR LS LT LU LV LY ' +
	'MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ ' +
	'NA NC NE NF NG NI NL NO NP NR NU NZ ' +
	'OM ' +
	'PA PE PF PG PH PK PL PM PN PR PS PT PW PY ' +
	'QA ' +
	'RE RO RS RU RW ' +
	'SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ ' +
	'TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ ' +
	'UA UG UM US UY UZ ' +
	'VA VC VE VG VI VN VU ' +
	'WF WS ' +
	'YE YT ' +
	'ZA ZM ZW';

export const RECOGNIZED_COUNTRY_CODES: ReadonlySet<string> = new Set(
	ISO_3166_1_ALPHA_2.trim().split(/\s+/),
);

// The IMS profile is unvalidated JSON, so the raw value can be anything —
// not just an unexpected string but a number, object, or null. Accept
// `unknown` and require exactly two ASCII letters BEFORE uppercasing: a
// non-string must fail open instead of throwing (an exception here would
// strand the user on the login screen, the lockout this gate must never
// cause), and Unicode full case mapping EXPANDS some single characters into
// valid codes ('ß' → 'SS', 'ﬁ' → 'FI') — values that aren't ISO codes and
// must therefore fail open, not block.
const normalizeCountryCode = (countryCode: unknown): string | null => {
	if (typeof countryCode !== 'string') {
		return null;
	}
	const trimmed = countryCode.trim();
	return /^[A-Za-z]{2}$/.test(trimmed) ? trimmed.toUpperCase() : null;
};

export const isCountryRecognized = (countryCode: unknown): boolean => {
	const code = normalizeCountryCode(countryCode);
	return code !== null && RECOGNIZED_COUNTRY_CODES.has(code);
};

// Blocking requires positive identification; everything else fails open. A
// missing, empty, malformed, or unrecognized countryCode must never lock
// anyone out — under a country allowlist, failing open is the only thing
// standing between an unexpected IMS value and a lockout (the failure mode
// that got the first rollout reverted). Callers log a warning on the missing
// and unrecognized cases so a silently degraded IMS field is still visible
// in Splunk.
export const isRegionBlocked = (countryCode?: unknown): boolean => {
	const code = normalizeCountryCode(countryCode);
	return (
		code !== null &&
		RECOGNIZED_COUNTRY_CODES.has(code) &&
		!ALLOWED_COUNTRY_CODES.has(code)
	);
};
